import axios from 'axios';

//Different URL for production and development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8095/api';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const customerService = {
  getAll: async (params) => {
    const response = await api.get('/customers', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/customers/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/customers', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/customers/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/customers/${id}`);
    return response.data;
  },

  resetPassword: async (id, data) => {
    const response = await api.post(`/customers/${id}/reset-password`, data);
    return response.data;
  },

  resendWelcomeEmail: async (id, data) => {
    const response = await api.post(`/customers/${id}/resend-welcome`, data);
    return response.data;
  },
};

export const categoryService = {
  getAll: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: true } : {};
    const response = await api.get('/categories', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/categories', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },

  reorder: async (categories) => {
    const response = await api.post('/categories/reorder', { categories });
    return response.data;
  },

  uploadIcon: async (formData) => {
    const response = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadPdf: async (categoryId, file, name) => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    const response = await api.post(`/categories/${categoryId}/pdfs`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deletePdf: async (categoryId, pdfId) => {
    const response = await api.delete(`/categories/${categoryId}/pdfs/${pdfId}`);
    return response.data;
  },
};

export const videoService = {
  getAll: async (params) => {
    const response = await api.get('/videos', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/videos/${id}`);
    return response.data;
  },

  create: async (formData) => {
    const response = await api.post('/videos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id, formData) => {
    const response = await api.put(`/videos/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/videos/${id}`);
    return response.data;
  },

  uploadPdf: async (videoId, file, name) => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    const response = await api.post(`/videos/${videoId}/pdfs`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deletePdf: async (videoId, pdfId) => {
    const response = await api.delete(`/videos/${videoId}/pdfs/${pdfId}`);
    return response.data;
  },

  togglePublic: async (id, isPublic) => {
    const response = await api.patch(`/videos/${id}`, { isPublic });
    return response.data;
  },

  getViewers: async (id, params) => {
    const response = await api.get(`/videos/${id}/viewers`, { params });
    return response.data;
  },
};

export const storyService = {
  getAll: async (params) => {
    const response = await api.get('/stories', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/stories/${id}`);
    return response.data;
  },

  create: async (formData) => {
    const response = await api.post('/stories', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id, formData) => {
    const response = await api.put(`/stories/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/stories/${id}`);
    return response.data;
  },

  reactivate: async (id) => {
    const response = await api.post(`/stories/${id}/reactivate`);
    return response.data;
  },

  getViewers: async (id, params) => {
    const response = await api.get(`/stories/${id}/viewers`, { params });
    return response.data;
  },
};


export const contactService = {
  getAll: async (params) => {
    console.log('📋 ContactService: Fetching all contacts with params:', params);
    const response = await api.get('/contacts', { params });
    console.log('📥 ContactService: Response:', response.data);
    return response.data;
  },

  getById: async (id) => {
    console.log('📋 ContactService: Fetching contact by ID:', id);
    const response = await api.get(`/contacts/${id}`);
    console.log('📥 ContactService: Response:', response.data);
    return response.data;
  },

  updateStatus: async (id, status) => {
    console.log('📋 ContactService: Updating status for contact:', id, 'to:', status);
    const response = await api.patch(`/contacts/${id}/status`, { status });
    console.log('📥 ContactService: Response:', response.data);
    return response.data;
  },

  delete: async (id) => {
    console.log('📋 ContactService: Deleting contact:', id);
    const response = await api.delete(`/contacts/${id}`);
    console.log('📥 ContactService: Response:', response.data);
    return response.data;
  },

  getStats: async () => {
    console.log('📊 ContactService: Fetching contact stats');
    const response = await api.get('/contacts/stats');
    console.log('📥 ContactService: Response:', response.data);
    return response.data;
  },
};

export const notificationService = {
  getAll: async (params) => {
    const response = await api.get('/notifications/all', { params });
    return response.data;
  },

  sendCustom: async (data) => {
    const response = await api.post('/notifications/send', data);
    return response.data;
  },

  sendForContent: async (data) => {
    const response = await api.post('/notifications/send-for-content', data);
    return response.data;
  },

  getHistory: async (userId, params) => {
    const response = await api.get(`/notifications/history/${userId}`, { params });
    return response.data;
  },

  markAsRead: async (id, userId) => {
    const response = await api.put(`/notifications/${id}/mark-read`, { userId });
    return response.data;
  },

  getUnreadCount: async (userId) => {
    const response = await api.get(`/notifications/unread-count/${userId}`);
    return response.data;
  },
};

export const settingsService = {
  getAppSettings: async () => {
    const response = await api.get('/app-settings');
    return response.data;
  },
  updateAppSettings: async (data) => {
    const response = await api.put('/app-settings', data);
    return response.data;
  },
};

export const securityService = {
  getCaptureAttempts: async (params) => {
    const response = await api.get('/security/capture-attempts', { params });
    return response.data;
  },
};

export const dashboardService = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
};

export const adminService = {
  getAll: async (params) => {
    const response = await api.get('/admins', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admins/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admins/register', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admins/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admins/${id}`);
    return response.data;
  },

  changePassword: async (id, data) => {
    const response = await api.post(`/admins/${id}/change-password`, data);
    return response.data;
  },

  changeOwnPassword: async (data) => {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  },
};

export default api;